import React, { useEffect, useState } from "react";
import {
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FaFileCircleExclamation,
  FaFileImport,
  FaFileCircleCheck,
  FaBuildingUser,
  FaUserTie,
} from "react-icons/fa6";
import { BarGraph, CountIconBox, PieGraph } from "../components";
import { ExtrasModel } from "../services/models";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";

export default function Home() {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pieData, setPieData] = useState([]);
  const [pieLoading, setPieLoading] = useState(true);
  const [barData, setBarData] = useState([]);
  const [barLoading, setBarLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    new DateObject().subtract(6, "days"),
    new DateObject(),
  ]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        let response = await ExtrasModel.getDataCount();
        if (response.status === 200) {
          let pendingNum = response.data.data.cocs.find(
            (obj) => obj._id === "pending"
          );
          let receivedNum = response.data.data.cocs.find(
            (obj) => obj._id === "received"
          );
          let analysedNum = response.data.data.cocs.find(
            (obj) => obj._id === "analysed"
          );
          let clientsNum = response.data.data.users.find(
            (obj) => obj._id === "user"
          );
          let techniciansNum = response.data.data.users.find(
            (obj) => obj._id === "technician"
          );
          let statsItems = [
            {
              icon: FaFileCircleExclamation,
              number: pendingNum ? pendingNum.count : "0",
              title: "Pending Orders",
            },
            {
              icon: FaFileImport,
              number: receivedNum ? receivedNum.count : "0",
              title: "Received Orders",
            },
            {
              icon: FaFileCircleCheck,
              number: analysedNum ? analysedNum.count : "0",
              title: "Analysed Orders",
            },
            {
              icon: FaBuildingUser,
              number: clientsNum ? clientsNum.count : "0",
              title: "Total Clients",
            },
            {
              icon: FaUserTie,
              number: techniciansNum ? techniciansNum.count : "0",
              title: "Total Technicians",
            },
          ];
          setStats(statsItems);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  useEffect(() => {
    const fetchPieData = async () => {
      try {
        let response = await ExtrasModel.getTodayCount();
        if (response.status === 200) {
          setPieData(response.data.data);
        }
      } catch (error) {
      } finally {
        setPieLoading(false);
      }
    };
    fetchPieData();
  }, []);

  const fetchBarData = async (startDate, endDate) => {
    try {
      setBarLoading(true);
      let response = await ExtrasModel.getRangeCount(startDate, endDate);
      if (response.status === 200) {
        setBarData(response.data.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setBarLoading(false);
    }
  };

  useEffect(() => {
    fetchBarData(
      dateRange[0].format("YYYY-MM-DD"),
      dateRange[1].format("YYYY-MM-DD")
    );
  }, []);

  return (
    <>
      <VStack alignItems="start">
        <VStack mt={{ base: "0", md: "3" }} mb="3" alignItems="center" w="full">
          <Heading textAlign="center" fontSize={{ base: "2xl", lg: "4xl" }}>
            Welcome To Restech Laboratories
          </Heading>
          <Text textAlign="center">
            Get your samples analyzed and download reports without hustle.
          </Text>
        </VStack>
        {loading ? (
          <Center w="full">
            <Spinner />
          </Center>
        ) : (
          <HStack
            gap="4"
            wrap="wrap"
            justifyContent={{ base: "center", md: "start" }}
          >
            {stats.map((item, i) => {
              return <CountIconBox {...item} key={i} />;
            })}
          </HStack>
        )}
      </VStack>
      <Grid
        templateColumns={{ md: "repeat(1, 1fr)", lg: "repeat(5, 1fr)" }}
        gap="4"
        mt="5"
      >
        <GridItem colSpan={{ md: 1, lg: 3 }}>
          <VStack
            alignItems="start"
            rounded="2xl"
            bgColor="white"
            shadow="md"
            py="4"
            px="4"
            h="300px"
          >
            <HStack
              flexDir={{ base: "column", md: "row" }}
              gap="2"
              justifyContent="space-between"
              w="full"
            >
              <Heading fontSize="lg">
                {dateRange[0].day +
                  " " +
                  dateRange[0].month.shortName +
                  " " +
                  dateRange[0].year}{" "}
                -{" "}
                {dateRange[1].day +
                  " " +
                  dateRange[1].month.shortName +
                  " " +
                  dateRange[1].year}
              </Heading>
              <DatePicker
                value={dateRange}
                onChange={(dateObjects) => {
                  if (!dateObjects || dateObjects.length === 0) {
                    setDateRange([]);
                    setBarData([]);
                    return;
                  }

                  const startObj = dateObjects[0];
                  const endObj = dateObjects[1] || dateObjects[0];

                  // Normalize range to always include two DateObjects to avoid downstream access errors
                  setDateRange([startObj, endObj]);

                  const startDate = startObj.format("YYYY-MM-DD");
                  const endDate = endObj.format("YYYY-MM-DD");
                  fetchBarData(startDate, endDate);
                }}
                dateSeparator=" - "
                className="rmdp-mobile"
                style={{
                  height: "30px",
                  padding: "8px",
                }}
                maxDate={new Date()}
                range
                rangeHover
              />
            </HStack>
            {barLoading ? (
              <Center w="full" h="full">
                <Spinner />
              </Center>
            ) : barData.length > 0 ? (
              <>
                <BarGraph data={barData} />
                <Flex gap={3} justifyContent="end" w="full">
                  <Box
                    bg="blue.50"
                    border="1px solid"
                    borderColor="blue.200"
                    borderRadius="md"
                    px={3}
                    py={2}
                  >
                    <Text fontSize="sm">
                      <Text as="span" fontWeight="bold" color="primary.600">Orders:</Text> {barData.reduce((total, item) => total + (item.orders || 0), 0)}
                    </Text>
                  </Box>
                  <Box
                    bg="orange.50"
                    border="1px solid"
                    borderColor="orange.200"
                    borderRadius="md"
                    px={3}
                    py={2}
                  >
                    <Text fontSize="sm">
                      <Text as="span" fontWeight="bold" color="orange.600">Samples:</Text> {barData.reduce((total, item) => total + (item.samples || 0), 0)}
                    </Text>
                  </Box>
                </Flex>
              </>
            ) : (
              <Center w="full" h="full" color="gray">
                No Orders
              </Center>
            )}
          </VStack>
        </GridItem>
        <GridItem colSpan={{ md: 1, lg: 2 }}>
          <VStack
            alignItems="start"
            rounded="2xl"
            bgColor="white"
            shadow="md"
            py="4"
            px="4"
            h="300px"
          >
            <Heading fontSize="lg">Today</Heading>
            {pieLoading ? (
              <Center w="full" h="full">
                <Spinner />
              </Center>
            ) : pieData.length > 0 ? (
              <PieGraph data={pieData} />
            ) : (
              <Center w="full" h="full" color="gray">
                No Orders
              </Center>
            )}
          </VStack>
        </GridItem>
      </Grid>
      {/* <Image
        src="/images/home-img2.png"
        alt=""
        w="200px"
        position="absolute"
        bottom={{ base: "100px", lg: "20px" }}
        right="30px"
        zIndex="0"
      /> */}
    </>
  );
}
